import { useEffect, useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { DayPicker, type DayPickerSingleProps, type ClassNames } from 'react-day-picker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { twMerge } from 'tailwind-merge';
import Lucide from '@/chassis/ui/components/icons/Lucide';
import 'react-day-picker/dist/style.css';
import { REQUIRED_FIELD_MESSAGE, REQUIRED_VALIDATION_EVENT, type RequiredValidationEventDetail } from '@/chassis/data/services/form-required-validation';
import RequiredBadge from '../../visual-helper/RequiredBadge';

dayjs.extend(customParseFormat);

export interface RadixDatePickerProps
    extends Omit<DayPickerSingleProps, 'mode' | 'selected' | 'onSelect' | 'classNames' | 'className'> {
    value?: Date | null;
    onChange?: (value?: Date | null) => void;
    placeholder?: string;
    displayFormat?: string;
    clearable?: boolean;
    disabled?: boolean;
    className?: string;
    contentClassName?: string;
    label?: string;
    helperText?: string;
    error?: string;
    showFooter?: boolean;
    side?: Popover.PopoverContentProps['side'];
    align?: Popover.PopoverContentProps['align'];
    sideOffset?: number;
    dayPickerClassNames?: ClassNames;
    id?: string;
    name?: string;
    renderHiddenInput?: boolean;
    required?: boolean;
    requiredMessage?: string;
}

const defaultDayPickerClassNames: ClassNames = {
    root: 'mx-auto text-slate-800 dark:text-slate-100',
    months: 'flex flex-col space-y-4',
    month: 'space-y-3',
    caption: 'flex items-center justify-between px-2',
    caption_label: 'text-sm font-semibold',
    caption_dropdowns: 'flex items-center gap-2',
    dropdown_month: 'relative inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-1 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/60 dark:border-darkmode-400 dark:bg-darkmode-700 dark:text-slate-100',
    dropdown_year: 'relative inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-1 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/60 dark:border-darkmode-400 dark:bg-darkmode-700 dark:text-slate-100',
    vhidden: 'sr-only',
    nav: 'flex items-center gap-2',
    nav_button:
        'inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:border-primary/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 dark:border-darkmode-400 dark:bg-darkmode-700 dark:text-slate-100 dark:hover:border-primary/60',
    table: 'w-full border-collapse text-sm',
    head_row: 'grid grid-cols-7 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-300',
    head_cell: 'text-center font-semibold',
    row: 'grid grid-cols-7',
    cell: 'text-center',
    day: 'mx-auto my-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium text-slate-800 transition hover:bg-primary/10 focus-visible:outline focus-visible:outline-primary dark:text-slate-100',
    day_selected: 'bg-primary text-white hover:bg-primary focus-visible:outline-white',
    day_today: 'border border-primary text-primary dark:text-primary',
    day_outside: 'text-slate-400 dark:text-slate-500',
    day_disabled: 'text-slate-300 line-through opacity-60 dark:text-slate-600',
};

function formatDisplay(value?: Date | null, displayFormat = 'DD/MM/YYYY') {
    if (!value) return '';
    return dayjs(value).isValid() ? dayjs(value).format(displayFormat) : '';
}

function RadixDatePicker({
    value,
    onChange,
    placeholder = 'Selecionar data',
    displayFormat = 'DD/MM/YYYY',
    clearable = true,
    disabled,
    className,
    contentClassName,
    label,
    helperText,
    error,
    showFooter = false,
    side = 'bottom',
    align = 'start',
    sideOffset = 8,
    dayPickerClassNames,
    id,
    name,
    renderHiddenInput = true,
    required,
    requiredMessage = REQUIRED_FIELD_MESSAGE,
    ...dayPickerProps
}: RadixDatePickerProps) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(() => formatDisplay(value, displayFormat));
    const inputName = renderHiddenInput ? undefined : name;
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [showRequiredError, setShowRequiredError] = useState(false);

    const formattedValue = useMemo(() => formatDisplay(value, displayFormat), [value, displayFormat]);
    const hasValue = Boolean(inputValue || formattedValue);

    useEffect(() => {
        setInputValue(formattedValue);
    }, [formattedValue]);

    useEffect(() => {
        if (!required) return undefined;

        const listener = (event: Event) => {
            const detail = (event as CustomEvent<RequiredValidationEventDetail>).detail;
            const targetForm = detail?.form;
            const element = inputRef.current;

            if (!element) return;
            if (targetForm && element.form && targetForm !== element.form) return;

            if (!element.value || element.value.trim() === '') {
                setShowRequiredError(true);
            }
        };

        window.addEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
        return () => window.removeEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
    }, [required]);

    const mergedClassNames = useMemo(() => ({ ...defaultDayPickerClassNames, ...dayPickerClassNames }), [dayPickerClassNames]);

    const applyDateMask = (raw: string, format: string) => {
        const digits = raw.replace(/\D/g, '');
        if (format.includes('/')) {
            // dd/MM/yyyy
            return digits
                .slice(0, 8)
                .replace(/(\d{2})(\d)/, '$1/$2')
                .replace(/(\d{2})(\d)/, '$1/$2')
                .replace(/(\d{4})(\d)/, '$1');
        }
        // yyyy-MM-dd (default)
        return digits
            .slice(0, 8)
            .replace(/(\d{4})(\d)/, '$1-$2')
            .replace(/(\d{2})(\d)/, '$1-$2')
            .replace(/(\d{4})(\d)/, '$1');
    };

    const handleSelect = (nextValue?: Date) => {
        onChange?.(nextValue ?? null);
        if (required) {
            setShowRequiredError(!nextValue);
        }
        setOpen(false);
    };

    const handleClear = (e?: React.MouseEvent) => {
        e?.stopPropagation?.();
        setInputValue('');
        onChange?.(null);
        if (required) {
            setShowRequiredError(true);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const masked = applyDateMask(event.target.value, displayFormat);
        setInputValue(masked);

        if (!masked) {
            onChange?.(null);
            if (required) {
                setShowRequiredError(true);
            }
            return;
        }

        // Only fire change when length matches format and parse is valid
        if (masked.length === displayFormat.length) {
            const parsed = dayjs(masked, displayFormat, true);
            if (parsed.isValid()) {
                onChange?.(parsed.toDate());
                if (required) {
                    setShowRequiredError(false);
                }
            }
        }
    };

    const handleBlur = () => {
        if (!required) return;
        setShowRequiredError(!inputValue || inputValue.trim() === '');
    };

    const inputError = error || (showRequiredError ? requiredMessage : undefined);
    const errorId = id ? `${id}-required` : `${(name || 'date')}-required`;

    return (
        <div className="space-y-1">
            {label ? (
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200 inline-flex items-center gap-1">
                    <span>{label}</span>
                    {required ? <RequiredBadge /> : null}
                </div>
            ) : null}

            <Popover.Root open={open} onOpenChange={setOpen}>
                <Popover.Anchor asChild>
                    <div className="relative" ref={anchorRef}>
                        <input
                            type="text"
                            id={id}
                            name={inputName}
                            value={inputValue}
                            onChange={handleInputChange}
                            onFocus={() => setOpen(true)}
                            onClick={() => setOpen(true)}
                            onBlur={handleBlur}
                            placeholder={placeholder}
                            disabled={disabled}
                            required={required}
                            aria-invalid={inputError ? 'true' : undefined}
                            aria-describedby={inputError ? errorId : undefined}
                            ref={inputRef}
                            className={twMerge(
                                'w-full h-10 rounded-md border border-slate-300/60 bg-white px-3 pr-10 text-sm text-slate-800 shadow-sm transition duration-200 ease-in-out',
                                'placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40',
                                'disabled:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400',
                                'dark:border-darkmode-400 dark:bg-darkmode-700 dark:text-slate-100 dark:focus:ring-slate-700 dark:focus:ring-opacity-50 dark:placeholder:text-slate-500/80 dark:disabled:bg-darkmode-700/50',
                                inputError && 'border-danger focus:border-danger focus:ring-danger/30',
                                className,
                            )}
                        />

                        <div className="pointer-events-none absolute inset-y-0 right-1.5 flex items-center gap-1 text-slate-500">
                            {clearable && hasValue && !disabled ? (
                                <button
                                    type="button"
                                    className="pointer-events-auto text-sm font-semibold text-slate-400 transition hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-200"
                                    onClick={() => handleClear()}
                                    aria-label="Limpar data"
                                >
                                    ×
                                </button>
                            ) : null}

                            <button
                                type="button"
                                className="pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                                disabled={disabled}
                                aria-label="Abrir calendário"
                                onClick={() => setOpen(true)}
                            >
                                <Lucide icon="Calendar" className="h-4 w-4" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </Popover.Anchor>

                <Popover.Content
                    side={side}
                    align={align}
                    sideOffset={sideOffset}
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    onCloseAutoFocus={(event) => event.preventDefault()}
                    onInteractOutside={(event) => {
                        const target = event.target as HTMLElement | null;
                        if (target && anchorRef.current?.contains(target)) {
                            event.preventDefault();
                            return;
                        }
                        // Prevent closing when interacting with native <select> dropdowns
                        // (e.g. month/year selectors from react-day-picker captionLayout="dropdown-buttons")
                        const active = document.activeElement;
                        if (active?.tagName === 'SELECT') {
                            event.preventDefault();
                        }
                    }}
                    onPointerDownOutside={(event) => {
                        const active = document.activeElement;
                        if (active?.tagName === 'SELECT') {
                            event.preventDefault();
                        }
                    }}
                    className={twMerge(
                        'z-50 w-[320px] rounded-xl border border-slate-200 bg-white p-3 shadow-xl outline-none dark:border-darkmode-400 dark:bg-darkmode-800',
                        contentClassName,
                    )}
                >
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2 dark:border-darkmode-500/70">
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Escolha a data</div>
                        <div className="flex items-center gap-2">
                            {clearable && hasValue ? (
                                <button
                                    type="button"
                                    className="text-xs font-semibold text-slate-500 transition hover:text-primary"
                                    onClick={() => onChange?.(null)}
                                >
                                    Limpar
                                </button>
                            ) : null}
                            <Popover.Close asChild>
                                <button
                                    type="button"
                                    className="flex h-8 items-center justify-center rounded-md border border-slate-200 px-2 text-xs font-semibold text-slate-700 transition hover:border-primary/50 hover:text-primary dark:border-darkmode-500/70 dark:text-slate-100"
                                >
                                    Fechar
                                </button>
                            </Popover.Close>
                        </div>
                    </div>

                    <DayPicker
                        mode="single"
                        selected={value ?? undefined}
                        onSelect={handleSelect}
                        weekStartsOn={1}
                        classNames={mergedClassNames}
                        {...dayPickerProps}
                    />

                    {showFooter ? (
                        <div className="mt-2 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-darkmode-700/60 dark:text-slate-300">
                            <div>
                                <span className="font-semibold">Selecionado:</span> {formattedValue || '—'}
                            </div>
                            <Popover.Close asChild>
                                <button type="button" className="text-xs font-semibold text-primary">
                                    Ok
                                </button>
                            </Popover.Close>
                        </div>
                    ) : null}
                </Popover.Content>
            </Popover.Root>

            {name && renderHiddenInput ? <input type="hidden" name={name} value={inputValue} aria-hidden required={required} /> : null}

            {helperText && !inputError ? <p className="text-xs text-slate-500 dark:text-slate-400">{helperText}</p> : null}
            {inputError ? <p id={errorId} className="text-xs font-semibold text-rose-600 dark:text-rose-400">{inputError}</p> : null}
        </div>
    );
}

export default RadixDatePicker;
